import { ReactNode } from "react";
import { Box, Checkbox, TableCell } from "@mui/material";
import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalTable,
    DotLegalTableState,
    DotLegalTooltip,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import FilterBar from "@common/components/filterBar/FilterBar";
import SavedViewsMenu from "@common/components/savedViews/SavedViewsMenu";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import { FilterPage } from "@common/pageView/PageView.types";
import { RiskFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import TableRow from "@common/components/tableRow/TableRow";
import DotLegalWarningTriangle from "@common/components/dotLegalWarningTriangle/DotLegalWarningTriangle.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { getRiskSettingsIcon } from "../../../riskSettings/common/RiskSettings.utils";
import IndicatorRiskChip from "../../IndicatorRiskChip";
import RiskTrajectory from "../../RiskTrajectory";
import RiskMitigationStatusChip from "../../RiskMitigationStatusChip";
import RiskTreatmentChip from "../../RiskTreatmentChip";
import { RiskTreatmentType } from "../../riskTreatmentType";
import { RiskAssessmentStatus } from "../../RiskAssessmentStatus";
import { formatExposureCost, formatExposureValue } from "../../riskExposure/formatExposureValue";
import RiskExposureBanner from "../../riskExposure/RiskExposureBanner";
import RiskCountCellLink from "../../riskControls/RiskCountCellLink";
import LinkedControlStatus from "../../riskControls/LinkedControlStatus";
import LinkedControlsDialog from "../../riskControls/LinkedControlsDialog";
import RiskAssessmentTimelineDialog from "../../riskHistoryOverview/riskAssessmentTimelineDialog/RiskAssessmentTimelineDialog";
import { RiskAssessmentColumn, RiskAssessmentSubject, RiskScenarioRow } from "../RiskAssessmentsOverview.types";
import {
    bulkMitigateDisabledReasonKey,
    getRowClickHandler,
    isMitigatable,
    isTreatable,
    scenarioSearchExcludedKeys,
    scenarioSortOverrides,
} from "../RiskAssessmentsOverview.utils";
import { BulkMitigationSelection, useBulkMitigationSelection } from "../useBulkMitigationSelection";
import { AssessDialog } from "../assessDialog/AssessDialog";
import MitigationPlanDialog from "../treatmentDialogs/MitigationPlanDialog";
import AcceptRiskDialog from "../treatmentDialogs/AcceptRiskDialog";
import AvoidRiskDialog from "../treatmentDialogs/AvoidRiskDialog";
import AddRiskScenarioDialog from "../addRiskScenarioDialog/AddRiskScenarioDialog";
import { DeleteScenarioDialog } from "../deleteScenarioDialog/DeleteScenarioDialog";
import RetireRiskAssessmentDialog from "../retireRiskAssessmentDialog/RetireRiskAssessmentDialog";
import BulkMitigationDialog from "../bulkMitigationDialog/BulkMitigationDialog";
import { useScenarioAssessmentsTable } from "./ScenarioAssessmentsTable.hooks";
import { useScenarioAssessmentsTableStyles } from "./ScenarioAssessmentsTable.styles";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import DotLegalNoteDialog from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";

export interface IScenarioAssessmentsTable {
    riskAreaId: string;
    // An assessed entity, or the organisation as a whole.
    subject: RiskAssessmentSubject;
    // Passed in only by a host that puts the Bulk mitigate / Cancel / Mitigate (n) buttons in its own toolbar.
    // Left out, the table keeps the selection to itself, which is what a host with no bulk buttons wants.
    bulkMitigation?: BulkMitigationSelection;
}

// The scenario assessments of one subject in one risk area: toolbar, table and every row-level dialog. Fetches
// its own data, so a host only needs to pass the area and the subject. Remount it (key) when the subject
// changes, since the saved view state is read once on mount.
function ScenarioAssessmentsTable({ riskAreaId, subject, bulkMitigation: hostBulkMitigation }: IScenarioAssessmentsTable) {
    const ownBulkMitigation = useBulkMitigationSelection();
    const bulkMitigation = hostBulkMitigation ?? ownBulkMitigation;
    const styles = useScenarioAssessmentsTableStyles();
    const { translateString, translateNumber } = useTranslation();
    const { addonsFeatures } = usePlanContext();

    const {
        isLoading,
        hasScenarios,
        subjectName,
        savedViewSecondaryScopeId,
        scenarioRows,
        mitigatableRows,
        isOverAppetite,
        getFilterDataOptions,
        onFilterChange,
        sortOrderBy,
        sortOrder,
        onSortChange,
        searchString,
        setSearchString,
        visibleColumns,
        renderedColumns,
        exposureSummary,
        exposureMode,
        exposureCurrencyCode,
        exposureScaleUpperBound,
        tableFields,
        onColumnsSave,
        onDownloadExcelReportClick,
        filterData,
        currentViewId,
        savedView,
        isSavedView,
        hasFilterChanged,
        hasDisplayChanged,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onAddScenario,
        isAddScenarioDialogOpen,
        closeAddScenarioDialog,
        onScenariosAdded,
        onAssess,
        assessingRow,
        closeAssessDialog,
        getNoteMenuItem,
        noteDialogProps,
        refetchOverview,
        onMitigate,
        onAcceptRisk,
        onAvoid,
        onGoToMitigation,
        onOpenCurrentTreatment,
        onDelete,
        mitigatingScenario,
        acceptingScenario,
        avoidingScenario,
        closeMitigateDialog,
        closeAcceptDialog,
        closeAvoidDialog,
        onMitigationSaved,
        onAcceptanceSaved,
        onAvoidanceSaved,
        deletingScenario,
        closeDeleteDialog,
        onScenarioDeleted,
        onRetire,
        onReinstate,
        onViewTimeline,
        timelineScenario,
        closeTimeline,
        onDocumentation,
        lifecycleScenario,
        closeLifecycleDialog,
        onLifecycleSaved,
        controlsDialogRow,
        openControlsDialog,
        closeControlsDialog,
        onBulkMitigationSaved,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = useScenarioAssessmentsTable(riskAreaId, subject, bulkMitigation);

    // The Risk column reads as a before -> after treatment trajectory: untreated scenarios show a single pill,
    // Mitigate points to its expected residual, Accept holds at the same level, and Avoid points to a grey
    // "Eliminated" pill, since the source is removed rather than reduced.
    const renderRiskTrajectory = (row: RiskScenarioRow) => {
        if (row.risk == null) return null;
        const current = (
            <>
                <IndicatorRiskChip
                    level={row.risk}
                    label={row.riskLabel}
                    riskLevelIndicators={row.riskLevelIndicators}
                    overAppetiteWarningText={isOverAppetite(row.risk) ? translateString("riskGovernanceOverAppetite") : undefined}
                />
            </>
        );

        switch (row.treatmentType) {
            case RiskTreatmentType.Mitigate:
                if (row.targetLabel == null || row.target == null) return current;
                return (
                    <RiskTrajectory
                        from={current}
                        to={<IndicatorRiskChip level={row.target} label={row.targetLabel} riskLevelIndicators={row.riskLevelIndicators} />}
                    />
                );
            case RiskTreatmentType.Accept:
                return (
                    <RiskTrajectory
                        from={current}
                        to={<IndicatorRiskChip level={row.risk} label={row.riskLabel} riskLevelIndicators={row.riskLevelIndicators} />}
                    />
                );
            case RiskTreatmentType.Avoid:
                return (
                    <RiskTrajectory
                        from={current}
                        to={
                            <IndicatorRiskChip
                                level={row.risk}
                                label={translateString("riskLevelEliminated")}
                                riskLevelIndicators={row.riskLevelIndicators}
                                eliminated
                            />
                        }
                    />
                );
            default:
                return current;
        }
    };

    const formatModeExposure = (value: number | null) =>
        formatExposureValue(value, exposureMode, exposureCurrencyCode, exposureScaleUpperBound, translateNumber);
    const formatCost = (value: number | null) => formatExposureCost(value, exposureCurrencyCode, translateNumber);

    const headerByColumn: Record<RiskAssessmentColumn, ITableHeader<RiskScenarioRow>> = {
        [RiskAssessmentColumn.Scenario]: { property: "name", text: translateString("scenario") },
        [RiskAssessmentColumn.Status]: { property: "statusLabel", text: translateString("assessmentStatus") },
        [RiskAssessmentColumn.Consequence]: { property: "consequenceLabel", text: translateString("consequence") },
        [RiskAssessmentColumn.Probability]: { property: "probabilityLabel", text: translateString("probability") },
        [RiskAssessmentColumn.Risk]: {
            property: "riskLabel",
            text: translateString("risk"),
            // Unassessed scenarios (no score yet) float to the top of the default view so the coverage gap is
            // visible; the scored ones follow, highest risk first. Flips symmetrically when toggled ascending.
            sortOverride: scenarioSortOverrides["riskLabel"],
        },
        [RiskAssessmentColumn.AssessmentDate]: {
            property: "assessmentDateLabel",
            text: translateString("assessmentDate"),
            sortOverride: scenarioSortOverrides["assessmentDateLabel"],
        },
        [RiskAssessmentColumn.AssessedBy]: { property: "assessedByName", text: translateString("assessedBy") },
        [RiskAssessmentColumn.Participants]: { property: "participantNamesLabel", text: translateString("participants") },
        [RiskAssessmentColumn.Treatment]: { property: "treatmentLabel", text: translateString("treatment") },
        [RiskAssessmentColumn.Mitigation]: { property: "mitigationStatusLabel", text: translateString("mitigation") },
        [RiskAssessmentColumn.ControlCount]: {
            property: "linkedControlCount",
            text: translateString("controlCount"),
            // No backing row field (the data is the linkedControlIds array), so sort by its length. Rows with no
            // linked controls float to the top in either direction, so the coverage gap stays visible.
            sortOverride: scenarioSortOverrides["linkedControlCount"],
        },
        [RiskAssessmentColumn.ControlStatus]: { property: "linkedControlStatus", text: translateString("controlStatus"), notSortable: true },
        [RiskAssessmentColumn.Documentation]: { property: "documentationCount", text: translateString("documentation") },
        [RiskAssessmentColumn.EstimatedLoss]: {
            property: "exposureValue",
            text: translateString("riskExposure"),
            sortOverride: scenarioSortOverrides["exposureValue"],
        },
        [RiskAssessmentColumn.EstimatedCost]: {
            property: "planEstimatedCost",
            text: translateString("estimatedCost"),
            sortOverride: scenarioSortOverrides["planEstimatedCost"],
        },
        [RiskAssessmentColumn.ExposureAfterMitigation]: {
            property: "planExpectedExposureValue",
            text: translateString("exposureAfterMitigationLabel"),
            sortOverride: scenarioSortOverrides["planExpectedExposureValue"],
        },
        [RiskAssessmentColumn.Note]: { property: "notePlainText", text: translateString("note") },
        [RiskAssessmentColumn.Confidence]: {
            property: "confidence",
            text: translateString("assessmentConfidence"),
            sortOverride: scenarioSortOverrides["confidence"],
        },
    };

    const headers: Array<ITableHeader<RiskScenarioRow>> = renderedColumns.map((column) => headerByColumn[column]);

    // A retired assessment is not open for assessment: the backend rejects scoring/mitigation, so only
    // reinstate and delete are offered. Non-retired rows keep the full action set.
    const getMenuItems = (row: RiskScenarioRow) => {
        // The timeline is read-only history, offered for any assessment that has been scored at least once
        // (an unscored one has nothing to show) — including retired ones.
        const timelineItem = row.assessedAt ? [{ menuItem: translateString("riskTimelineView"), onClick: () => onViewTimeline(row) }] : [];
        // The note sits on the assessment rather than on a revision, so it stays editable after retirement too.
        const noteItem = getNoteMenuItem(row);

        return row.status === RiskAssessmentStatus.Retired
            ? [
                  ...timelineItem,
                  noteItem,
                  { menuItem: translateString("reinstate"), onClick: () => onReinstate(row) },
                  { menuItem: translateString("delete"), onClick: () => onDelete(row) },
              ]
            : [
                  { menuItem: translateString("assess"), onClick: () => onAssess(row) },
                  ...timelineItem,
                  { menuItem: translateString("documentation"), onClick: () => onDocumentation(row) },
                  noteItem,
                  { menuItem: translateString("retire"), onClick: () => onRetire(row) },
                  { menuItem: translateString("delete"), onClick: () => onDelete(row) },
                  ...(row.treatmentType === RiskTreatmentType.Mitigate && row.mitigationPlanStatus != null
                      ? [{ menuItem: translateString("riskGoToMitigation"), onClick: () => onGoToMitigation(row) }]
                      : []),
                  ...(row.risk != null
                      ? [
                            { menuItem: translateString("treatments"), isHeader: true },
                            { menuItem: translateString("mitigate"), onClick: () => onMitigate(row) },
                            { menuItem: translateString("riskMitigationActionAcceptRisk"), onClick: () => onAcceptRisk(row) },
                            { menuItem: translateString("riskTreatmentTypeAvoid"), onClick: () => onAvoid(row) },
                        ]
                      : []),
              ];
    };

    // Both treatment warnings (drift, stale completed mitigation) open the current-treatment dialog on click,
    // exactly like the chips next to them.
    const renderTreatmentWarning = (row: RiskScenarioRow, warningText: ReactNode) => (
        <Box
            sx={{ cursor: isTreatable(row) ? "pointer" : "default", display: "flex" }}
            onClick={(e) => {
                if (isTreatable(row)) {
                    e.stopPropagation();
                    onOpenCurrentTreatment(row);
                }
            }}
        >
            <DotLegalWarningTriangle warningText={warningText} />
        </Box>
    );

    const renderCell = (column: RiskAssessmentColumn, row: RiskScenarioRow) => {
        const canTreat = isTreatable(row);

        switch (column) {
            case RiskAssessmentColumn.Scenario:
                return <TableCell key={column}>{row.name}</TableCell>;
            case RiskAssessmentColumn.Status:
                return <TableCell key={column}>{row.statusLabel}</TableCell>;
            case RiskAssessmentColumn.Consequence:
                return <TableCell key={column}>{row.consequenceLabel}</TableCell>;
            case RiskAssessmentColumn.Probability:
                return <TableCell key={column}>{row.probabilityLabel}</TableCell>;
            case RiskAssessmentColumn.Risk:
                return (
                    <TableCell key={column}>
                        <Box sx={styles.riskCell}>{renderRiskTrajectory(row)}</Box>
                    </TableCell>
                );
            case RiskAssessmentColumn.AssessmentDate:
                return <TableCell key={column}>{row.assessmentDateLabel}</TableCell>;
            case RiskAssessmentColumn.AssessedBy:
                return <TableCell key={column}>{row.assessedByName}</TableCell>;
            case RiskAssessmentColumn.Participants:
                return <TableCell key={column}>{row.participantNamesLabel}</TableCell>;
            case RiskAssessmentColumn.Treatment:
                return (
                    <TableCell key={column}>
                        <Box sx={styles.treatmentCell}>
                            {row.treatmentType !== RiskTreatmentType.None && (
                                <RiskTreatmentChip
                                    treatmentType={row.treatmentType}
                                    onClick={
                                        canTreat
                                            ? (e) => {
                                                  e.stopPropagation();
                                                  onOpenCurrentTreatment(row);
                                              }
                                            : undefined
                                    }
                                />
                            )}
                            {row.isTreatmentDrifted && renderTreatmentWarning(row, row.driftWarningText)}
                            {row.isCompletedMitigationStale && renderTreatmentWarning(row, row.staleWarningText)}
                        </Box>
                    </TableCell>
                );
            case RiskAssessmentColumn.Mitigation:
                return (
                    <TableCell key={column}>
                        {row.treatmentType === RiskTreatmentType.Mitigate && row.mitigationPlanStatus != null && (
                            <RiskMitigationStatusChip
                                mitigationPlanStatus={row.mitigationPlanStatus}
                                onClick={
                                    canTreat
                                        ? (e) => {
                                              e.stopPropagation();
                                              onOpenCurrentTreatment(row);
                                          }
                                        : undefined
                                }
                            />
                        )}
                    </TableCell>
                );
            case RiskAssessmentColumn.ControlCount:
                return (
                    <TableCell key={column} align="right">
                        <RiskCountCellLink count={row.linkedControlIds.length} onClick={() => openControlsDialog(row)} />
                    </TableCell>
                );
            case RiskAssessmentColumn.ControlStatus:
                return (
                    <TableCell key={column}>
                        <LinkedControlStatus status={row.linkedControlStatus} />
                    </TableCell>
                );
            case RiskAssessmentColumn.Documentation:
                return (
                    <TableCell key={column} align="right">
                        <RiskCountCellLink count={row.documentationCount} onClick={() => onDocumentation(row)} showZero />
                    </TableCell>
                );
            case RiskAssessmentColumn.EstimatedLoss:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(row.exposureValue)}
                    </TableCell>
                );
            case RiskAssessmentColumn.EstimatedCost:
                return (
                    <TableCell key={column} align="right">
                        {formatCost(row.planEstimatedCost)}
                    </TableCell>
                );
            case RiskAssessmentColumn.ExposureAfterMitigation:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(row.planExpectedExposureValue)}
                    </TableCell>
                );
            case RiskAssessmentColumn.Note:
                return (
                    <TableCell key={column}>
                        <TruncatedMentionText value={row.note} />
                    </TableCell>
                );
            case RiskAssessmentColumn.Confidence:
                return <TableCell key={column}>{row.confidenceLabel}</TableCell>;
        }
    };

    return (
        <>
            <TablePagePaper>
                {/* An entity subject states its exposure in the page's top-bar tiles, so only the organisation,
                    which has no top bar of its own, carries the headline over the table. */}
                {subject.kind === "organisation" && (
                    <RiskExposureBanner
                        exposure={exposureSummary}
                        exposureMode={exposureMode}
                        exposureCurrencyCode={exposureCurrencyCode}
                        exposureScaleUpperBound={exposureScaleUpperBound}
                    />
                )}

                {hasScenarios ? (
                    <>
                        <ListPageToolbar
                            savedViewsMenu={
                                <SavedViewsMenu
                                    currentViewId={currentViewId}
                                    parentIsLoading={isLoading}
                                    currentFilterData={filterData}
                                    currentDisplayData={visibleColumns}
                                    onViewChange={onChangeView}
                                    onViewSaved={onViewSaved}
                                    onViewDeleted={onViewDeleted}
                                    filterPage={FilterPage.RiskAssessmentsOverview}
                                    scopeId={riskAreaId}
                                    secondaryScopeId={savedViewSecondaryScopeId}
                                    hasFilterOrDisplayDataChanged={hasFilterChanged || hasDisplayChanged}
                                />
                            }
                            leftContent={
                                <FilterBar
                                    isLoading={isLoading}
                                    filterData={getFilterDataOptions()}
                                    fetchFilterData
                                    onMenuClose={onFilterChange}
                                    onRemove={onFilterChange}
                                    pinnedFilterIds={pinnedFilterIds}
                                    onPinnedFilterIdsChange={setPinnedFilterIds}
                                    searchValue={searchString}
                                    onSearchChange={setSearchString}
                                    searchLabel={translateString("search")}
                                />
                            }
                            rightContent={
                                <>
                                    {savedView && (
                                        <TableDisplayFields
                                            selectedFields={visibleColumns}
                                            onSave={onColumnsSave}
                                            options={tableFields}
                                            hasDisplayChanged={hasDisplayChanged}
                                            isCustomView={isSavedView}
                                            savedViewFields={savedView.displayData}
                                            groupingOrder={RiskFieldGroupingOrder}
                                        />
                                    )}
                                    {savedView && (
                                        <ExcelExportButton
                                            onClick={onDownloadExcelReportClick}
                                            isDisabled={isLoading}
                                            isPlanLocked={!addonsFeatures.customLists}
                                            planLockedText={translateString("upgradeToCustomList")}
                                        />
                                    )}
                                </>
                            }
                        />

                        <DotLegalTable
                            // Sorting is uncontrolled (defaultOrderBy/defaultOrder only apply on mount), so remount
                            // when a different saved view is applied to pick up that view's persisted sort.
                            key={currentViewId ?? "system-default"}
                            hasOverflowMenu={!bulkMitigation.isSelectionMode}
                            headers={headers}
                            defaultOrderBy={sortOrderBy as keyof RiskScenarioRow}
                            defaultOrder={sortOrder}
                            onChange={(state: DotLegalTableState<RiskScenarioRow>) => {
                                if (state.property) onSortChange({ orderBy: String(state.property), order: state.order });
                            }}
                            data={scenarioRows}
                            isLoading={isLoading}
                            searchString={searchString}
                            columnsExcludedFromFilter={scenarioSearchExcludedKeys}
                            rowsPerPage={50}
                            paginationLabelOf={translateString("labelOf")}
                            emptyText={translateString("noRiskScenariosMatchesQuery")}
                            clickableRows
                            onSelectAllChanged={bulkMitigation.isSelectionMode ? () => bulkMitigation.onSelectAllChanged(mitigatableRows) : undefined}
                            headerCheckBoxState={bulkMitigation.isSelectionMode ? bulkMitigation.getHeaderCheckBoxState(mitigatableRows) : undefined}
                            renderRow={(row) => {
                                const isRetired = row.status === RiskAssessmentStatus.Retired;
                                const isSelectable = isMitigatable(row);
                                const onRowClick = getRowClickHandler({
                                    isSelectionMode: bulkMitigation.isSelectionMode,
                                    isSelectable,
                                    isRetired,
                                    onToggle: () => bulkMitigation.toggle(row),
                                    onAssess: () => onAssess(row),
                                });
                                return (
                                    <TableRow key={row.id} onClick={onRowClick} sx={{ cursor: onRowClick ? "pointer" : "default" }}>
                                        {bulkMitigation.isSelectionMode && (
                                            <TableCell padding="checkbox">
                                                {isSelectable ? (
                                                    <Checkbox checked={bulkMitigation.isSelected(row)} readOnly tabIndex={-1} />
                                                ) : (
                                                    // A disabled checkbox doesn't emit hover events, so the span makes the tooltip reachable.
                                                    // The theme forces every checkbox icon blue, so grey the icon explicitly to read as disabled.
                                                    <DotLegalTooltip text={translateString(bulkMitigateDisabledReasonKey(row)!)}>
                                                        <span>
                                                            <Checkbox
                                                                checked={false}
                                                                disabled
                                                                readOnly
                                                                tabIndex={-1}
                                                                sx={{ "& .MuiSvgIcon-root": { fill: (theme) => theme.palette.grey[400] } }}
                                                            />
                                                        </span>
                                                    </DotLegalTooltip>
                                                )}
                                            </TableCell>
                                        )}
                                        {renderedColumns.map((column) => renderCell(column, row))}
                                        {!bulkMitigation.isSelectionMode && (
                                            <TableCell align={"right"} onClick={(e) => e.stopPropagation()}>
                                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            }}
                        />
                    </>
                ) : (
                    <DotLegalEmptyState icon={getRiskSettingsIcon()} text={translateString("addScenarioToGetStarted")} />
                )}

                {!bulkMitigation.isSelectionMode && (
                    <Box sx={styles.addButtonContainer}>
                        <DotLegalButton buttonType="primary" onClick={onAddScenario}>
                            {translateString("addScenario")}
                        </DotLegalButton>
                    </Box>
                )}
            </TablePagePaper>

            {isAddScenarioDialogOpen && (
                <AddRiskScenarioDialog riskAreaId={riskAreaId} subject={subject} onCloseDialog={closeAddScenarioDialog} onScenariosAdded={onScenariosAdded} />
            )}
            {bulkMitigation.isDialogOpen && (
                <BulkMitigationDialog
                    scenarios={bulkMitigation.selectedScenarios}
                    onRemoveScenario={bulkMitigation.removeSelected}
                    onSaved={onBulkMitigationSaved}
                    onClose={bulkMitigation.closeDialog}
                />
            )}
            {mitigatingScenario && <MitigationPlanDialog scenario={mitigatingScenario} onClose={closeMitigateDialog} onSaved={onMitigationSaved} />}
            {acceptingScenario && <AcceptRiskDialog scenario={acceptingScenario} onClose={closeAcceptDialog} onSaved={onAcceptanceSaved} />}
            {avoidingScenario && <AvoidRiskDialog scenario={avoidingScenario} onClose={closeAvoidDialog} onSaved={onAvoidanceSaved} />}

            {deletingScenario && <DeleteScenarioDialog scenario={deletingScenario} onClose={closeDeleteDialog} onDeleted={onScenarioDeleted} />}

            {assessingRow && <AssessDialog row={assessingRow} riskAreaId={riskAreaId} onSaved={refetchOverview} onClose={closeAssessDialog} />}

            {noteDialogProps && <DotLegalNoteDialog {...noteDialogProps} />}

            {lifecycleScenario && (
                <RetireRiskAssessmentDialog
                    action={lifecycleScenario.action}
                    scenario={lifecycleScenario.scenario}
                    onClose={closeLifecycleDialog}
                    onSaved={onLifecycleSaved}
                />
            )}

            {timelineScenario && (
                <RiskAssessmentTimelineDialog
                    riskAssessmentId={timelineScenario.id}
                    entityName={subjectName}
                    scenarioName={timelineScenario.name}
                    onClose={closeTimeline}
                />
            )}

            {controlsDialogRow && <LinkedControlsDialog linkedControlIds={controlsDialogRow.linkedControlIds} onClose={closeControlsDialog} />}
        </>
    );
}

export default ScenarioAssessmentsTable;
